import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { CalendarClock, Phone, RefreshCw } from 'lucide-react'
import { z } from 'zod'

import { ApiError, errorMessage } from '@/api/errors'
import { useRenewSubscription, useSubscriptionScheduleView } from '@/api/queries'
import type { GymSubscriptionListItem } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { formatDate, plural } from '@/lib/format'

/**
 * Lo scadenzario — ISC-05.
 *
 * ## Tre domande, non un elenco con dei filtri
 *
 * Le domande che una palestra si fa sono tre, e sono in tab perché la differenza va
 * vista e non cercata: **chi scade** nei prossimi trenta giorni, **chi è scaduto e non
 * ha rinnovato**, **chi sta finendo il blocchetto**. Ognuna corrisponde a una
 * telefonata diversa.
 *
 * `renewed` lo calcola il server: un'iscrizione è rinnovata se un'altra la nomina come
 * precedente. Senza quel campo «scadute e non rinnovate» darebbe l'elenco di tutti i
 * clienti mai avuti, ed è la ragione per cui il filtro non si può fare nel client.
 *
 * ## Perché la riga porta il telefono
 *
 * Perché l'azione che segue uno scadenzario è una chiamata. Senza il numero servirebbe
 * aprire una scheda per riga — cioè trenta schede per fare trenta telefonate.
 *
 * ## Il rinnovo qui è lo stesso di là
 *
 * Un clic, corpo vuoto, e il server ricopia tutto facendo partire la nuova iscrizione
 * il giorno dopo la scadenza. La riga rinnovata **resta** nell'elenco fino al
 * ricaricamento: non è un difetto — l'iscrizione che scade non cambia, ne nasce
 * un'altra —, e il badge «rinnovata» è ciò che lo dice.
 */
const VIEWS = ['scadenza', 'nonRinnovati', 'carnet'] as const
type View = (typeof VIEWS)[number]

const VIEW_LABELS: Record<View, string> = {
  scadenza: 'Scadono a breve',
  nonRinnovati: 'Scaduti, non rinnovati',
  carnet: 'Carnet in esaurimento',
}

const searchSchema = z.object({
  view: z.enum(VIEWS).optional(),
  /** Giorni di finestra per «scadono a breve». Trenta è il default del settore. */
  days: z.coerce.number().int().min(1).max(365).optional(),
})

export const Route = createFileRoute('/_app/scadenzario/')({
  validateSearch: searchSchema,
  component: SchedulePage,
})

function SchedulePage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const view: View = search.view ?? 'scadenza'
  const days = search.days ?? 30

  // Le date le calcola l'hook **dentro la queryFn**: `new Date()` durante il render è
  // una chiamata impura, e una data ricalcolata a ogni disegno renderebbe la chiave di
  // query diversa a ogni disegno — cioè la cache inutile.
  const list = useSubscriptionScheduleView(view, days)
  const rows = list.data?.data ?? []

  return (
    <>
      <PageHeader
        title="Scadenzario"
        description="Chi chiamare, e per cosa. Ordinato per scadenza: chi scade prima sta in cima."
      />

      <Tabs
        variant="pill"
        value={view}
        onValueChange={(value) =>
          void navigate({ search: (prev) => ({ ...prev, view: value as View }) })
        }
        className="mb-4"
      >
        <TabsList>
          {VIEWS.map((item) => (
            <TabsTrigger key={item} value={item}>
              {VIEW_LABELS[item]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {view === 'scadenza' ? (
        <div className="mb-3 flex flex-wrap gap-2">
          {[7, 15, 30, 60].map((option) => (
            <Button
              key={option}
              size="sm"
              variant={days === option ? 'primary' : 'outline'}
              onClick={() => void navigate({ search: (prev) => ({ ...prev, days: option }) })}
            >
              {option} giorni
            </Button>
          ))}
        </div>
      ) : null}

      {list.isError && !list.data ? (
        <ErrorState
          message={errorMessage(list.error)}
          requestId={list.error instanceof ApiError ? list.error.requestId : undefined}
          onRetry={() => void list.refetch()}
        />
      ) : list.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<CalendarClock />}
          title="Niente da chiamare"
          description={
            view === 'scadenza'
              ? `Nessuna iscrizione scade nei prossimi ${days} giorni.`
              : view === 'nonRinnovati'
                ? 'Nessuno è scaduto senza rinnovare.'
                : 'Nessun carnet sta finendo.'
          }
        />
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {rows.map((row) => (
              <ScheduleRow key={row.id} row={row} />
            ))}
          </ul>
          <p className="text-muted-foreground pt-3 text-xs" data-numeric>
            {plural(rows.length, 'iscrizione', 'iscrizioni')}
          </p>
        </>
      )}

      {view === 'nonRinnovati' ? (
        <Alert variant="neutral" className="mt-4" title="Come si calcola «non rinnovata»">
          Un'iscrizione è rinnovata quando **un'altra** la nomina come precedente, e lo stabilisce
          il server: al pannello servirebbe l'intero storico di ogni persona per dedurlo, e senza
          quel dato questo elenco sarebbe la lista di tutti i clienti mai avuti.
        </Alert>
      ) : null}
    </>
  )
}

function ScheduleRow({ row }: { row: GymSubscriptionListItem }) {
  const renew = useRenewSubscription()

  return (
    <li>
      <Card radius="md" padding="sm" className="flex flex-wrap items-center gap-3">
        <Link
          to="/iscritti/$gymMemberId"
          params={{ gymMemberId: row.gymMemberId }}
          className="min-w-0 flex-1"
        >
          <p className="truncate font-medium">
            {row.gymMemberLastName} {row.gymMemberFirstName}
          </p>
          <p className="text-muted-foreground text-xs">
            {row.productName}
            {row.endsOn !== null ? ` · scade il ${formatDate(row.endsOn)}` : ''}
            {row.entriesRemaining !== null
              ? ` · ${plural(row.entriesRemaining, 'ingresso residuo', 'ingressi residui')}`
              : ''}
          </p>
        </Link>

        {row.gymMemberPhone !== null ? (
          <a
            href={`tel:${row.gymMemberPhone}`}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs"
          >
            <Phone className="size-3" />
            {row.gymMemberPhone}
          </a>
        ) : (
          <span className="text-muted-foreground text-xs">nessun telefono</span>
        )}

        {row.autoRenew ? <Badge variant="neutral">rinnovo automatico</Badge> : null}
        {row.renewed ? (
          <Badge variant="success">rinnovata</Badge>
        ) : (
          <Button
            size="sm"
            variant="outline"
            loading={renew.isPending}
            onClick={() => renew.mutate({ gymSubscriptionId: row.id })}
          >
            <RefreshCw />
            Rinnova
          </Button>
        )}
      </Card>
    </li>
  )
}
